import { ChangeEvent, Dispatch, SetStateAction } from 'react'

import { useMutation } from '@apollo/client'
import { Icon, SquareDotLoadingIndicator } from '@joor/design-system'
import isEmpty from 'lodash/isEmpty'
import styled, { css } from 'styled-components'

import {
  Attachment,
  UploadAttachmentToS3Document,
} from '__generated__/atlas-types'

import { cleanGraphQLErrors } from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'
import { useRenderToasters } from 'hooks/useRenderToasters'

import { sendPaymentRequestIds } from '../../orders.ids'

interface PropsType {
  attachmentLoading?: boolean
  customAttachLabel?: string
  setUploadedFiles?: Dispatch<SetStateAction<Attachment[]>>
  handleUploading?: (attachment: Attachment) => void
}

const MAX_FILE_SIZE_MB = 50
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024
const MAX_FILE_SIZE_ERROR_MSG = `Attached file size exceeds ${MAX_FILE_SIZE_MB}MB file size limit`

const FileUploaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const LoaderStyled = styled(SquareDotLoadingIndicator)`
  //TODO: add sizes to the loader in the DS
  transform: scale(0.6);
  > div {
    margin-top: 0 !important;
  }

  &.without-label > div {
    margin-left: -190px !important;
  }
`

export const StyledLabel = styled.label<{ disabled: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  column-gap: 8px;
  color: var(--color-secondary-800);
  svg {
    --main-color: var(--color-secondary-800);
  }
  &:hover {
    color: var(--color-secondary-600);
    text-decoration: underline;
    svg {
      --main-color: var(--color-secondary-600);
    }
  }
  ${({ disabled }) =>
    disabled &&
    css`
      ,
      &:hover {
        color: var(--color-neutral-300);
        text-decoration: none;
        cursor: default;
        svg {
          --main-color: var(--color-neutral-300);
        }
      }
    `}
`

export const StyledFileInput = styled.input`
  opacity: 0;
  position: absolute;
  z-index: -1;
`

export const AttachmentsContainer = styled.div`
  margin: 5px 0;
  a {
    color: var(--color-secondary-800);
    display: block;
    &:hover {
      color: var(--color-secondary-600);
      text-decoration: underline;
    }
  }
`

const FileUploader = ({
  customAttachLabel,
  attachmentLoading,
  setUploadedFiles,
  handleUploading,
}: PropsType) => {
  const { renderErrorToast } = useRenderToasters()
  const [uploadFiles, { loading }] = useMutation(UploadAttachmentToS3Document, {
    client: atlasClient,
    errorPolicy: 'none',
    onError: ({ graphQLErrors }) => {
      const errors = cleanGraphQLErrors(graphQLErrors)
      renderErrorToast({ description: errors[0] })
    },
    onCompleted: (resp) => {
      if (!resp.uploadAttachmentToS3?.fileName) return
      if (handleUploading) {
        // this code was added to handle the new case but needs a refactor
        // once we get rid of the external payments modal, returning always the value and not the setter
        handleUploading({
          url: resp.uploadAttachmentToS3?.url ?? '',
          fileName: resp.uploadAttachmentToS3?.fileName ?? '',
        })
        return
      }
      if (setUploadedFiles) {
        setUploadedFiles((prev) => [
          ...prev,
          {
            url: resp.uploadAttachmentToS3?.url ?? '',
            fileName: resp.uploadAttachmentToS3?.fileName ?? '',
          },
        ])
      }
    },
  })
  const onFileSelectHandler = async (
    event: ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    if (isEmpty(event.target.files)) return
    const file = event.target.files![0]
    if (file.size > MAX_FILE_SIZE) {
      renderErrorToast({ description: MAX_FILE_SIZE_ERROR_MSG })
    } else {
      await uploadFiles({
        variables: {
          input: {
            file,
          },
        },
      })
    }
    event.target.value = ''
  }
  return (
    <FileUploaderContainer>
      {/*
        am hard-coding it because i dont want to have an import from a
        place that's too far away from this. will see if i can find a better
        place to put it later
      */}
      <StyledLabel
        id={sendPaymentRequestIds.attachInvoice}
        htmlFor="upload-attachments"
        disabled={loading || !!attachmentLoading}
      >
        <Icon iconName="attachment" plain iconSize="small" />
        {customAttachLabel ? customAttachLabel : 'Attach Invoice'}
      </StyledLabel>
      <StyledFileInput
        type="file"
        id="upload-attachments"
        onChange={onFileSelectHandler}
        accept=".pdf, image/*"
        disabled={loading || !!attachmentLoading}
      />
      {loading && (
        <LoaderStyled className={!customAttachLabel ? 'without-label' : ''} />
      )}
    </FileUploaderContainer>
  )
}

export default FileUploader
